import {
  ChangelogEntryPageContent,
  DocsPageContent,
  isDocsAdminToolsEnabled,
  AdminToolbar,
  ChangelogSetPageContent,
} from '@squareup/dex-feature-docs-page';
import {
  MdxPageContent,
  type MdxPageMetadata,
} from '@squareup/dex-feature-mdx-renderer';
import { Box } from '@squareup/dex-ui-shared-base';
import { isProductionEnvironment } from '@squareup/dex-utils-environment';
import type { GetServerSidePropsContext } from 'next';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import { ReactNode, useEffect, useMemo } from 'react';

import { DocsBasePage, DocsBasePageProps } from '../../shared/DocsBasePage';
import { getInitialPropsDocPage } from '../../shared/docs-get-initial-props';

type DocsBasePagePropsWithMdx = DocsBasePageProps & {
  mdxMode?: boolean;
  mdxSource?: MDXRemoteSerializeResult;
  mdxMetadata?: MdxPageMetadata;
  hideDocsTools?: boolean;
};
type DocsInitialProps = Awaited<ReturnType<typeof getInitialPropsDocPage>> & {
  data?: {
    doc?: DocsBasePagePropsWithMdx;
  };
  returnVal?: {
    data?: {
      doc?: DocsBasePagePropsWithMdx;
    };
  };
};

const Page = (data: DocsBasePagePropsWithMdx) => {
  const page = data.pageInView?.page;

  // Visible served-via signal: lets us tell from the browser console whether
  // the page in view was rendered from the MDX payload or the Contentful
  // pipeline. Fires on initial hydration AND every SPA navigation (the slug
  // dep changes when next/link routes to a new docs page). Logs the LD
  // `mdxFlag` snapshot alongside (read from pageProps.data.mdxFlag — see
  // _app.tsx which spreads serverInitialResponse onto the page Component, so
  // `data.data.mdxFlag` here equals `pageProps.data.mdxFlag`) so we can
  // sanity-check the snapshot threading from PR #1909.
  const mdxFlagSnapshot =
    (data as { data?: { mdxFlag?: unknown } }).data?.mdxFlag ?? null;
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const slug = page?.slug ?? window.location.pathname;
    const servedVia = data.mdxMode === true ? 'MDX' : 'Contentful';
    const color = data.mdxMode === true ? '#22c55e' : '#3b82f6';
    // eslint-disable-next-line no-console
    console.log(
      `%c[mdx] %c${servedVia}%c ${slug}`,
      'color:#888',
      `color:${color};font-weight:600`,
      'color:inherit',
      { mdxFlag: mdxFlagSnapshot }
    );
  }, [data.mdxMode, page?.slug, mdxFlagSnapshot]);

  const adminToolbar: ReactNode = useMemo(() => {
    return (
      <AdminToolbar
        features={{
          showMarkdownEnabled: true,
          viewDiffEnabled: !data.releaseTrain && !data.preview,
          viewContentfulEntryEnabled: true,
          showPreviewEnabled: !data.releaseTrain,
          releaseTrainEnabled: !data.preview,
          showHtmlHighlightEnabled: true,
          contentfulEnvironmentEnabled: !data.releaseTrain,
          unpublishedOasEnabled: true,
        }}
        contentfulEntryId={page?.id}
        isPreview={data.preview}
      />
    );
  }, [data?.preview, data?.releaseTrain, page?.id]);

  if (data.mdxMode && data.mdxSource && data.mdxMetadata) {
    return (
      <MdxPageContent
        mdxSource={data.mdxSource}
        metadata={data.mdxMetadata}
        navSet={data?.navSet?.value || undefined}
      />
    );
  }

  if (!page) {
    return null;
  }

  let pageContent: ReactNode;

  if (page.type === 'changelog-entry-page') {
    pageContent = (
      <ChangelogEntryPageContent
        page={page}
        navSet={data?.navSet?.value || undefined}
      ></ChangelogEntryPageContent>
    );
  } else if (page.type === 'changelog-set-page') {
    pageContent = (
      <ChangelogSetPageContent page={page}></ChangelogSetPageContent>
    );
  } else {
    pageContent = (
      <DocsPageContent
        // This forces the useState calls to reset when we get pages,
        // especially the ones in the `useRequirements` hook since it uses
        // default state.
        // Otherwise, the internal `useRequirements` call will no-op, and you will
        // not fetch new requirements
        key={page.id}
        docPage={page}
        navSet={data?.navSet?.value || undefined}
        requirementDomain={data?.pageInView?.requirementsDomain || undefined}
        contentfulEnvironment={data.contentfulEnvironment}
        releaseTrain={data.releaseTrain}
      ></DocsPageContent>
    );
  }

  return (
    <>
      {pageContent}
      {!data.hideDocsTools && isDocsAdminToolsEnabled() && adminToolbar}
    </>
  );
};

// Visible "served via" pill rendered top-right of the viewport. Lets a
// non-engineer (e.g. the original ANT-138 reporter, who can't open DevTools to
// inspect `__NEXT_DATA__`) confirm at a glance which pipeline produced the
// page they're looking at, including after every SPA navigation. Gated by
// `!isProductionEnvironment()` so the pill is invisible to ALL production
// traffic (public and internal). This is intentionally stricter than the
// `isDocsAdminToolsEnabled()` gate that AdminToolbar uses — AdminToolbar is
// designed to be visible to internal Block users in prod for debugging, but
// the served-via pill is purely a staging/dev diagnostic and should never
// appear in prod regardless of who is browsing.
const ServedViaPill = ({
  mdxMode,
  slug,
  mdxFlag,
}: {
  mdxMode: boolean | undefined;
  slug: string | undefined;
  mdxFlag: unknown;
}) => {
  const isMdx = mdxMode === true;
  const switchTarget = isMdx ? 'Contentful' : 'MDX';
  const switchServedVia = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('mdx', isMdx ? 'no' : 'yes');
    window.location.assign(url.toString());
  };

  return (
    <Box
      as="button"
      type="button"
      onClick={switchServedVia}
      data-testid="served-via-pill"
      data-served-via={isMdx ? 'mdx' : 'contentful'}
      title={`Click to switch to ${switchTarget}. mdxFlag: ${JSON.stringify(
        mdxFlag
      )}`}
      style={{
        position: 'fixed',
        top: 58,
        right: 8,
        zIndex: 99999,
        margin: 0,
        padding: '4px 8px',
        border: 0,
        borderRadius: 4,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
        fontSize: 11,
        lineHeight: 1.2,
        fontWeight: 600,
        color: 'white',
        background: isMdx ? '#22c55e' : '#3b82f6',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      {isMdx ? 'MDX' : 'Contentful'}
      {slug ? ` · ${slug}` : ''}
    </Box>
  );
};

const WrapperPage = (data: DocsBasePagePropsWithMdx) => {
  const slug = data.pageInView?.page?.slug;
  const mdxFlag =
    (data as { data?: { mdxFlag?: unknown } }).data?.mdxFlag ?? null;
  return (
    <>
      <DocsBasePage {...data}>
        <Page {...data} />
      </DocsBasePage>
      {!isProductionEnvironment() && (
        <ServedViaPill mdxMode={data.mdxMode} slug={slug} mdxFlag={mdxFlag} />
      )}
    </>
  );
};

WrapperPage.getInitialProps = async (ctx: GetServerSidePropsContext) => {
  const props = (await getInitialPropsDocPage(ctx)) as DocsInitialProps;
  const hideDocsToolsQuery = ctx.query['hide_docs_tools'];
  const hideDocsTools =
    typeof hideDocsToolsQuery === 'string' &&
    ['1', 'true', 'yes'].includes(hideDocsToolsQuery);

  if (props.data?.doc) {
    return {
      ...props,
      data: {
        ...props.data,
        doc: {
          ...props.data.doc,
          hideDocsTools,
        },
      },
    };
  }

  if (props.returnVal?.data?.doc) {
    return {
      ...props,
      returnVal: {
        ...props.returnVal,
        data: {
          ...props.returnVal.data,
          doc: {
            ...props.returnVal.data.doc,
            hideDocsTools,
          },
        },
      },
    };
  }

  return props;
};

export default WrapperPage;
